import { FolderOpen, ImagePlus, Trash2, Video } from 'lucide-react';
import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { deleteLocalFile, saveLocalFile } from '../../lib/localMedia';
import { newId, TRANSITIONS, useSettings, type MediaItem } from '../../store/settings';
import { buttonClass, Choice, ghostButtonClass, inputClass, Section, Toggle } from './controls';
import { OneDrivePicker } from './OneDrivePicker';

// Slider positions map onto these durations: fine control at the short end, up to 60 min.
export const INTERVAL_STEPS = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 300, 600, 900, 1200, 1800, 2700, 3600];

export function formatInterval(sec: number): string {
  if (sec < 60) return `${sec} seconds`;
  const m = sec / 60;
  return m === 1 ? '1 minute' : `${m % 1 ? m.toFixed(1) : m} minutes`;
}

const VIDEO_EXT = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

function kindFor(url: string, mime?: string): MediaItem['kind'] {
  if (mime) return mime.startsWith('video/') ? 'video' : 'image';
  return VIDEO_EXT.test(url) ? 'video' : 'image';
}

export function MediaTab() {
  const media = useSettings((s) => s.media);
  const update = useSettings((s) => s.update);
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);

  const setItems = (items: MediaItem[]) => update('media', { items });

  const addUrl = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    let parsed: URL;
    try {
      parsed = new URL(url.trim());
    } catch {
      setError('That doesn’t look like a web address.');
      return;
    }
    if (parsed.protocol !== 'https:') {
      setError('Use an https:// address.');
      return;
    }
    setItems([...media.items, { id: newId(), url: parsed.toString(), kind: kindFor(parsed.pathname) }]);
    setUrl('');
  };

  const addFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter((f) => /^(image|video)\//.test(f.type));
    e.target.value = '';
    setError(null);
    const added: MediaItem[] = [];
    let unsaved = 0;
    for (const f of files) {
      const id = newId();
      try {
        await saveLocalFile(id, f);
      } catch {
        unsaved++; // quota or private mode – still show it for this session
      }
      added.push({ id, url: URL.createObjectURL(f), kind: kindFor(f.name, f.type), title: f.name, local: true });
    }
    setItems([...useSettings.getState().media.items, ...added]);
    if (unsaved) setError(`${unsaved} file(s) couldn’t be stored on this device and will disappear after a reload.`);
  };

  const remove = (item: MediaItem) => {
    if (item.local) {
      if (item.url) URL.revokeObjectURL(item.url);
      void deleteLocalFile(item.id).catch(() => undefined);
    }
    setItems(media.items.filter((i) => i.id !== item.id));
  };

  const stepIndex = INTERVAL_STEPS.reduce(
    (best, s, i) => (Math.abs(s - media.intervalSec) < Math.abs((INTERVAL_STEPS[best] ?? 0) - media.intervalSec) ? i : best),
    0,
  );

  return (
    <>
      <OneDrivePicker />
      <Section
        title="Photos & videos"
        description="Add web addresses or files from this device. Google Photos, iCloud and Amazon Photos are coming."
      >
        <form onSubmit={addUrl} className="flex gap-2">
          <input
            className={inputClass}
            placeholder="https://… (image, .mp4, .webm or .mov)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            aria-label="Media address"
          />
          <button className={buttonClass} type="submit">
            Add
          </button>
        </form>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex flex-wrap gap-2">
          <button className={ghostButtonClass} onClick={() => fileInput.current?.click()} type="button">
            <ImagePlus className="h-4 w-4" aria-hidden /> Add files
          </button>
          <button className={ghostButtonClass} onClick={() => folderInput.current?.click()} type="button">
            <FolderOpen className="h-4 w-4" aria-hidden /> Add a folder
          </button>
          <input ref={fileInput} type="file" accept="image/*,video/*" multiple hidden onChange={addFiles} />
          <input
            ref={(el) => {
              folderInput.current = el;
              el?.setAttribute('webkitdirectory', '');
            }}
            type="file"
            multiple
            hidden
            onChange={addFiles}
          />
        </div>
        <p className="muted text-xs">Files from this device are stored on this screen only and are never uploaded.</p>
        {media.items.length === 0 ? (
          <p className="muted text-sm">No media yet – showing the demo video (or the built-in ambient scenes if it’s switched off).</p>
        ) : (
          <ul className="grid grid-cols-3 gap-2">
            {media.items.map((item) => (
              <li key={item.id} className="group relative aspect-video overflow-hidden rounded-lg bg-black/40">
                {item.kind === 'image' ? (
                  <img src={item.url} alt={item.title ?? ''} className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <Video className="h-6 w-6 opacity-70" aria-hidden />
                  </div>
                )}
                <button
                  onClick={() => remove(item)}
                  className="absolute right-1 top-1 rounded-md bg-black/60 p-1 text-white opacity-0 transition group-hover:opacity-100 focus:opacity-100"
                  aria-label="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Slideshow">
        <label className="block">
          <div className="mb-1 flex justify-between text-sm">
            <span>Time per slide</span>
            <span className="muted">{formatInterval(media.intervalSec)}</span>
          </div>
          <input
            type="range"
            min={0}
            max={INTERVAL_STEPS.length - 1}
            value={stepIndex}
            onChange={(e) => update('media', { intervalSec: INTERVAL_STEPS[Number(e.target.value)] ?? 30 })}
            className="w-full accent-[var(--accent)]"
          />
        </label>
        <Choice options={TRANSITIONS} value={media.transition} onChange={(transition) => update('media', { transition })} />
        <Toggle label="Shuffle" checked={media.shuffle} onChange={(shuffle) => update('media', { shuffle })} />
        <Toggle
          label="Fill the screen (crops the edges of photos that don’t match the screen shape)"
          checked={media.fit === 'cover'}
          onChange={(fill) => update('media', { fit: fill ? 'cover' : 'contain' })}
        />
        <Toggle
          label="Only photos that match the screen’s shape (landscape photos on landscape layouts, portrait on portrait)"
          checked={media.matchOrientation}
          onChange={(matchOrientation) => update('media', { matchOrientation })}
        />
        <Toggle
          label="Demo video when no photos are chosen (otherwise the built-in ambient scenes)"
          checked={media.demo}
          onChange={(demo) => update('media', { demo })}
        />
      </Section>

      <Section
        title="Photo quality"
        description="For cloud photos. Original is sharpest on big 4K TVs; choose a lower size if photos load slowly."
      >
        <Choice
          options={[
            { id: 'original', name: 'Original', description: 'Full resolution' },
            { id: '4k', name: '4K', description: 'Up to 3840×2160' },
            { id: '1080', name: 'Full HD', description: 'Up to 1920×1080' },
          ]}
          value={media.quality}
          onChange={(quality) => update('media', { quality })}
          columns={3}
        />
      </Section>

      <Section title="Shading overlay" description="Tints the media so widgets stay readable over bright photos.">
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={media.overlayColor}
            onChange={(e) => update('media', { overlayColor: e.target.value })}
            className="h-10 w-14 cursor-pointer rounded border border-[var(--border)] bg-transparent"
            aria-label="Overlay colour"
          />
          <label className="flex-1">
            <div className="mb-1 flex justify-between text-sm">
              <span>Opacity</span>
              <span className="muted">{Math.round(media.overlayOpacity * 100)}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round(media.overlayOpacity * 100)}
              onChange={(e) => update('media', { overlayOpacity: Number(e.target.value) / 100 })}
              className="w-full accent-[var(--accent)]"
            />
          </label>
        </div>
      </Section>
    </>
  );
}
